import { zodResolver } from "@hookform/resolvers/zod";
import { api } from "@slapinc/slappos-backend/api";
import {
	createFileRoute,
	useNavigate,
	useSearch,
} from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import type { GenericId as Id } from "convex/values";
import { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { BranchSelector } from "@/components/branch-selector";
import { PageContent } from "@/components/layout/page-content";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EmptyStateWithSkeleton } from "@/components/ui/empty-state-with-skeleton";
import {
	Field,
	FieldDescription,
	FieldError,
	FieldGroup,
	FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { useAutoSelectBranch } from "@/hooks/use-auto-select-branch";
import { useMe } from "@/hooks/use-me";
import { handleConvexError } from "@/lib/errors";
import { toast } from "@/lib/toast";

interface BranchSettingsSearch {
	branch?: string;
}

export const Route = createFileRoute("/(dashboard)/branch/settings/")({
	component: BranchSettingsPage,
	validateSearch: (search: Record<string, unknown>): BranchSettingsSearch => ({
		branch: search.branch as string | undefined,
	}),
});

const updateBranchSchema = z.object({
	name: z.string().trim().min(1, "Branch name is required").max(100),
	address: z.string().optional(),
	staffCommissionRate: z.number().min(0).max(100).optional(),
});

type UpdateBranchInput = z.infer<typeof updateBranchSchema>;

function BranchSettingsPage() {
	const navigate = useNavigate();
	const { branch: searchBranchId } = useSearch({
		from: "/(dashboard)/branch/settings/",
	});

	const me = useMe();
	const orgId = me?.activeOrganizationId;

	const branchesData = useQuery(
		api.features.branches.web.list,
		orgId ? { orgId } : "skip",
	);
	const branches = branchesData ?? [];

	const { branchId, isLoading: isLoadingBranches } = useAutoSelectBranch({
		branchId: searchBranchId,
		branches: branchesData,
		orgId,
	});

	const branchData =
		branchesData === undefined
			? undefined
			: (branchesData.find((b) => b._id === branchId) ?? null);

	const updateBranch = useMutation(api.features.branches.web.update);

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors, isSubmitting, isDirty },
	} = useForm<UpdateBranchInput>({
		resolver: zodResolver(updateBranchSchema),
		defaultValues: { name: "", address: "", staffCommissionRate: undefined },
	});

	useEffect(() => {
		if (branchData) {
			reset({
				name: branchData.name,
				address: branchData.address ?? "",
				staffCommissionRate: branchData.staffCommissionRate,
			});
		}
	}, [branchData, reset]);

	const onSubmit = async (data: UpdateBranchInput) => {
		if (!orgId || !branchId) return;
		try {
			await updateBranch({
				orgId,
				branchId: branchId as Id<"branches">,
				name: data.name,
				address: data.address || undefined,
				staffCommissionRate: data.staffCommissionRate,
			});
			toast.success(<>Branch settings saved</>);
		} catch (error) {
			handleConvexError(error);
		}
	};

	const handleBranchChange = useCallback(
		(value: Id<"branches"> | Id<"branches">[] | undefined) => {
			if (typeof value !== "string") return;
			navigate({ to: ".", search: { branch: value } });
		},
		[navigate],
	);

	const controls = (
		<BranchSelector
			branches={branches}
			value={branchId as Id<"branches"> | undefined}
			onValueChange={handleBranchChange}
			placeholder="Select branch"
			className="w-48"
			onViewAll={() => navigate({ to: "/settings/branches" })}
			onCreateNew={() => navigate({ to: "/settings/branches" })}
		/>
	);

	if (!branchId && !isLoadingBranches) {
		return (
			<PageContent title="Branch Settings" controls={controls}>
				<div className="flex flex-1 flex-col justify-end pb-12">
					<EmptyStateWithSkeleton
						title="No branches found"
						description="Create a branch to configure its settings."
						actionLabel="Create branch"
						onAction={() => navigate({ to: "/settings/branches" })}
						icon={null}
					/>
				</div>
			</PageContent>
		);
	}

	if (branchData === undefined) {
		return (
			<PageContent title="Branch Settings" controls={controls}>
				<div className="flex items-center justify-center py-12">
					<Spinner />
				</div>
			</PageContent>
		);
	}

	return (
		<PageContent title="Branch Settings" controls={controls}>
			<div className="space-y-6">
				<div className="flex flex-col gap-2">
					<h2 className="text-2xl font-bold">General</h2>
					<p className="text-muted-foreground">
						Manage branch name, address, and staff commission settings.
					</p>
				</div>

				<form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
					<Card className="p-4 sm:p-6">
						<h3 className="text-lg font-semibold mb-4">Branch Info</h3>
						<FieldGroup>
							<Field data-invalid={!!errors.name}>
								<FieldLabel htmlFor="name">Branch name</FieldLabel>
								<Input
									id="name"
									placeholder="e.g., Main Store"
									{...register("name")}
									aria-invalid={!!errors.name}
								/>
								<FieldError>{errors.name?.message}</FieldError>
							</Field>

							<Field data-invalid={!!errors.address}>
								<FieldLabel htmlFor="address">Address (optional)</FieldLabel>
								<FieldDescription>
									Full address of this branch location.
								</FieldDescription>
								<Textarea
									id="address"
									placeholder="123 Main St, City, Country"
									{...register("address")}
									aria-invalid={!!errors.address}
								/>
								<FieldError>{errors.address?.message}</FieldError>
							</Field>
						</FieldGroup>
					</Card>

					<Card className="p-4 sm:p-6">
						<h3 className="text-lg font-semibold mb-4">Staff</h3>
						<FieldGroup>
							<Field data-invalid={!!errors.staffCommissionRate}>
								<FieldLabel htmlFor="staffCommissionRate">
									Commission % (optional)
								</FieldLabel>
								<FieldDescription>
									Default staff commission rate for this branch.
								</FieldDescription>
								<Input
									id="staffCommissionRate"
									type="number"
									step="any"
									min={0}
									max={100}
									placeholder="e.g., 5"
									{...register("staffCommissionRate", {
										setValueAs: (v) => (v === "" ? undefined : Number(v)),
									})}
									aria-invalid={!!errors.staffCommissionRate}
								/>
								<FieldError>{errors.staffCommissionRate?.message}</FieldError>
							</Field>
						</FieldGroup>
					</Card>

					<div className="flex justify-end">
						<Button type="submit" disabled={!isDirty || isSubmitting}>
							{isSubmitting ? "Saving..." : "Save changes"}
						</Button>
					</div>
				</form>
			</div>
		</PageContent>
	);
}
